// src/components/layout/Navbar.tsx
import React, { useEffect, useRef } from 'react';
import { Bell, Menu, User, Settings, LogOut, ChevronDown, Lock } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useSidebarStore } from '../../store/sidebarStore';
import { useNotificationStore } from '../../store/notificationStore';
import { authService } from '../../services/auth';
import { useSystemSettingsStore } from '../../store/systemSettingsStore';
import { formatDistanceToNow } from 'date-fns';
import { id } from 'date-fns/locale';
import { getAvatarUrl } from '../../utils/url';

const Navbar: React.FC = () => {
  const { user, logout } = useAuthStore();
  const { isExpanded, setMobileOpen } = useSidebarStore();
  const { settings } = useSystemSettingsStore();
  const {
    notifications, unreadCount, isDropdownOpen,
    toggleDropdown, closeDropdown, markAllAsRead, markAsRead, fetchNotifications
  } = useNotificationStore();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = React.useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  // State for avatar error handling
  const [avatarError, setAvatarError] = React.useState(false);

  // Reset error when user or updated_at changes
  useEffect(() => {
    setAvatarError(false);
  }, [user?.avatar, user?.updated_at]);

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
    // Optional: Poll every minute
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) closeDropdown();
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setProfileOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closeDropdown]);

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (e) {
      // ignore
    }
    logout();
    navigate('/login');
  };

  const initials = user?.name?.split(/[\s-]+/).filter(Boolean).map(n => n[0]).join('').substring(0, 2).toUpperCase() || 'U';

  const avatarUrl = getAvatarUrl(user?.avatar, user?.updated_at);
  const showAvatar = avatarUrl && !avatarError;

  const handleNotificationClick = (notificationId: string, isRead: boolean) => {
    if (!isRead) {
      void markAsRead(notificationId);
    }

    closeDropdown();
  };

  return (
    <header
      className={`
        fixed top-0 right-0 h-16 bg-white border-b border-neutral-border z-30
        flex items-center justify-between px-4 lg:px-6
        transition-all duration-300
        ${isExpanded ? 'lg:left-64' : 'lg:left-16'} left-0
      `}
    >
      {/* Left: Mobile menu + Title */}
      <div className="flex items-center gap-3">
        <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-neutral-bg">
          <Menu className="w-5 h-5" />
        </button>
        <h1 className="text-lg font-bold text-teal-dark tracking-tight hidden sm:block">
          {settings.app_name || 'SIM-EVAL'}
        </h1>
      </div>

      {/* Right: Notifications + Profile */}
      <div className="flex items-center gap-2">
        {/* Notification Bell */}
        <div ref={notifRef} className="relative">
          <button
            onClick={toggleDropdown}
            className="relative p-2 rounded-lg hover:bg-neutral-bg transition-colors"
          >
            <Bell className="w-5 h-5 text-neutral-text-secondary" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-danger text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Notification Dropdown */}
          {isDropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border border-neutral-border overflow-hidden">
              <div className="px-4 py-3 border-b border-neutral-border flex items-center justify-between">
                <h3 className="text-sm font-bold text-neutral-text-primary">Notifikasi</h3>
                {unreadCount > 0 && (
                  <button onClick={markAllAsRead} className="text-xs text-teal-main hover:underline">
                    Tandai semua dibaca
                  </button>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-neutral-text-secondary">
                    Tidak ada notifikasi
                  </div>
                ) : (
                  notifications.slice(0, 8).map((notif) => (
                    <Link
                      key={notif.id}
                      to={notif.data.url || '/notifications'}
                      onClick={() => handleNotificationClick(notif.id, Boolean(notif.read_at))}
                      className={`
                        block px-4 py-3 border-b border-neutral-border last:border-0
                        hover:bg-neutral-bg transition-colors
                        ${!notif.read_at ? 'bg-blue-50/50' : ''}
                      `}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${!notif.read_at ? 'bg-teal-main' : 'bg-transparent'}`} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${!notif.read_at ? 'font-semibold' : ''} text-neutral-text-primary truncate`}>
                            {notif.data.title}
                          </p>
                          <p className="text-xs text-neutral-text-secondary mt-0.5 line-clamp-2">
                            {notif.data.message}
                          </p>
                          <p className="text-[10px] text-neutral-text-secondary mt-1">
                            {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true, locale: id })}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))
                )}
              </div>
              <Link
                to="/notifications"
                onClick={closeDropdown}
                className="block px-4 py-3 text-center text-xs font-semibold text-teal-main hover:bg-neutral-bg border-t border-neutral-border"
              >
                Lihat Semua Notifikasi
              </Link>
            </div>
          )}
        </div>

        {/* Profile Dropdown */}
        <div ref={profileRef} className="relative">
          <button
            onClick={() => setProfileOpen(!profileOpen)}
            className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-neutral-bg transition-colors"
          >
            <div className="w-8 h-8 rounded-full overflow-hidden bg-teal-main text-white flex items-center justify-center text-xs font-bold">
              {showAvatar ? (
                <img
                  src={avatarUrl!}
                  alt={user?.name || 'Avatar'}
                  className="w-full h-full object-cover"
                  onError={() => setAvatarError(true)}
                />
              ) : (
                initials
              )}
            </div>
            <div className="hidden sm:block text-left">
              <p className="text-sm font-semibold text-neutral-text-primary leading-tight">{user?.name}</p>
              <p className="text-[10px] text-neutral-text-secondary">{user?.role?.display_name}</p>
            </div>
            <ChevronDown className="w-4 h-4 text-neutral-text-secondary hidden sm:block" />
          </button>

          {/* Profile Menu */}
          {profileOpen && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-2xl border border-neutral-border overflow-hidden">
              <div className="px-4 py-3 border-b border-neutral-border">
                <p className="text-sm font-bold text-neutral-text-primary">{user?.name}</p>
                <p className="text-xs text-neutral-text-secondary">{user?.email}</p>
              </div>
              <div className="py-1">
                <Link
                  to="/profile"
                  onClick={() => setProfileOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-text-primary hover:bg-neutral-bg transition-colors"
                >
                  <User className="w-4 h-4" /> Profil Saya
                </Link>
                <Link
                  to="/settings"
                  onClick={() => setProfileOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-text-primary hover:bg-neutral-bg transition-colors"
                >
                  <Settings className="w-4 h-4" /> Pengaturan
                </Link>
                <Link
                  to="/change-password"
                  onClick={() => setProfileOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-text-primary hover:bg-neutral-bg transition-colors"
                >
                  <Lock className="w-4 h-4" /> Ubah Password
                </Link>
              </div>
              <div className="border-t border-neutral-border py-1">
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-danger hover:bg-red-50 transition-colors"
                >
                  <LogOut className="w-4 h-4" /> Keluar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
